import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import { renderText } from '@/app/module/cati/helpers'

import CatiAnswerMultiple from './cati-answer-multiple'
import CatiAnswerOpenEnded from './cati-answer-open-ended'
import CatiAnswerSingle from './cati-answer-single'
import { SpacingContainer } from './common'

export type Props = {
  message: string
  multiSelect?: boolean
  openEnded?: boolean
  stepNumber: number
  selectedAnswers: string[]
  type: 'catiMessage' | 'catiQuestion'
  validAnswers?: string[]
  onAnswersChange: (answers: string[]) => void
}

const CATIStepCard: React.FC<Props> = ({
  message,
  multiSelect,
  openEnded,
  selectedAnswers,
  stepNumber = 1,
  type,
  validAnswers,
  onAnswersChange,
}) => {
  const selectedAnswer = selectedAnswers?.[0] || ''
  const handleAnswerChange = React.useCallback(
    (answer: string) => {
      onAnswersChange([answer])
    },
    [onAnswersChange],
  )

  const subtitle = React.useMemo(() => {
    if (type === 'catiMessage') return 'Read to the contact. Continue when acknowledged.'
    if (openEnded) return 'Type the answer below.'
    if (multiSelect) return 'Select all answers that apply.'
    return 'Select one answer.'
  }, [type, openEnded, multiSelect])

  return (
    <StepCard>
      <CardContent sx={{ height: '100%', overflow: 'auto', padding: 2, width: '100%' }}>
        <StepToolbar>
          <StepHeaderContainer>
            <StepNumberTypography variant="h6">
              {`${stepNumber}. ${type === 'catiMessage' ? 'Message' : 'Question'}`}
            </StepNumberTypography>
            <StepSubtitleTypography color="textSecondary" variant="caption">
              {subtitle}
            </StepSubtitleTypography>
          </StepHeaderContainer>
        </StepToolbar>
        <SpacingContainer spacing={1}>
          <Typography variant="h5" color="textSecondary">
            {renderText(message)}
          </Typography>
        </SpacingContainer>
        {openEnded && <CatiAnswerOpenEnded selectedAnswer={selectedAnswer} onAnswerChange={handleAnswerChange} />}
        {validAnswers && !multiSelect && (
          <CatiAnswerSingle
            selectedAnswer={selectedAnswer}
            validAnswers={validAnswers}
            onAnswerChange={handleAnswerChange}
          />
        )}
        {validAnswers && multiSelect && (
          <CatiAnswerMultiple
            selectedAnswers={selectedAnswers}
            validAnswers={validAnswers}
            onAnswersChange={onAnswersChange}
          />
        )}
      </CardContent>
    </StepCard>
  )
}

const StepCard = styled(Card)({
  display: 'flex',
})

const StepToolbar = styled('div')({
  margin: '-16px -16px 0px',
  backgroundColor: '#f5f5f5',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 16,
})

const StepNumberTypography = styled(Typography)({
  width: '100%',
})

const StepHeaderContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
})

const StepSubtitleTypography = styled(Typography)({
  width: '100%',
})

export default CATIStepCard
